/**
 * LoginPage - Owner Portal Authentication Entry Point
 * 
 * Purpose:
 * Provides the login form interface for the Owner Portal. This component is now presentation-only;
 * all business logic (authentication, state management, error handling) has been extracted to
 * the useLogin hook. Displays login form UI, password visibility toggle, and error messages.
 * 
 * High-level Workflow:
 * 1. User enters email and password in the login form
 * 2. On form submission, credentials are sent to /api/v1/owner/auth/login endpoint
 * 3. Backend validates credentials and sets a session cookie (owner.sid) if valid
 * 4. If login succeeds, component calls /api/v1/owner/auth/me to verify the cookie was set
 * 5. If mustResetPassword flag is true, user is redirected to /owner/reset-password
 * 6. Otherwise, onLoginSuccess callback is invoked with user data and user is navigated to /calendar
 * 7. If login fails, error message is displayed based on server-provided error code
 * 
 * Assumptions & Invariants:
 * - onLoginSuccess prop is provided by parent (owner-main.jsx) and is a function that accepts
 *   user/owner data from /me endpoint response
 * - Backend /api/v1/owner/auth/login endpoint returns response with outcome field ('success' or 'error')
 * - Backend /api/v1/owner/auth/me endpoint returns user/owner data at top level (not nested in data)
 * - Session cookie (owner.sid) is set by backend and automatically included in subsequent requests
 * - Navigation target after successful login is /calendar (hardcoded)
 * - Error codes from backend: ROLE_NOT_PERMITTED, OWNER_LINK_MISSING, ACCOUNT_INACTIVE
 * - Component is rendered within OwnerLayout (from owner-main.jsx routing)
 * 
 * Dependencies:
 * Internal:
 *   - hooks/useLogin.js: Login state and submission logic
 *   - styles/loginPageStyles.js: Style definitions for login form UI
 *   - react-router-dom: useNavigate() hook for programmatic navigation (forgot password link)
 * External:
 *   - react: useState hook for password visibility toggle state
 *   - lucide-react: Eye, EyeOff icons for password visibility toggle
 * 
 * Version: 1.2.0
 * 
 * Changelog:
 * - 2026-01-26: Phase 1 - Added charter-compliant file header and function documentation
 * - 2026-01-26: Phase 2 - Extracted business logic to useLogin hook and loginErrorMessages util
 * - 2026-01-26: Phase 3 - Extracted styles object to loginPageStyles.js module
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { useLogin } from '../../hooks/useLogin.js';
import { getLoginPageStyles } from '../../styles/loginPageStyles.js';
import { useViewport } from '../../contexts/ViewportContext.jsx';
import { isAllowedRedirectUrl } from '../../utils/siteLoginRedirect.js';

export default function LoginPage({ onLoginSuccess }) {
  const navigate = useNavigate();
  const { isMobile } = useViewport();
  const styles = getLoginPageStyles(isMobile);
  const [showPassword, setShowPassword] = useState(false);
  const { email, setEmail, password, setPassword, error, loading, handleSubmit, siteAccessDenied } = useLogin(onLoginSuccess);
  const redirectTimerRef = useRef(null);
  const redirectUrlRef = useRef(null);

  // Clear redirect timer only on unmount so a later state reset cannot cancel the redirect
  useEffect(() => {
    return () => {
      if (redirectTimerRef.current) clearTimeout(redirectTimerRef.current);
    };
  }, []);

  useEffect(() => {
    const url = siteAccessDenied?.suggestedLoginUrl;
    if (!url) return;
    if (!isAllowedRedirectUrl(url)) {
      console.warn('[LoginPage] SITE_ACCESS_DENIED suggestedLoginUrl not allowlisted; not redirecting', { url });
      return;
    }
    if (redirectTimerRef.current !== null) return; // already scheduled
    redirectUrlRef.current = url;
    redirectTimerRef.current = window.setTimeout(() => {
      const target = redirectUrlRef.current;
      if (target) window.location.assign(target);
      redirectTimerRef.current = null;
    }, 2000);
    // Intentionally no cleanup: do not clear timer when deps change so redirect still fires
  }, [siteAccessDenied?.suggestedLoginUrl]);

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.header}>
          <h1 style={styles.title}>Welcome back</h1>
          <p style={styles.subtitle}>Sign in to access your Owner Portal</p>
        </div>
        
        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.field}>
            <label htmlFor="email" style={styles.label}>
              Email address
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
              autoComplete="email"
              style={{
                ...styles.input,
                ...(loading ? styles.inputDisabled : {}),
              }}
              aria-describedby={error ? 'login-error' : undefined}
            />
          </div>
          
          <div style={styles.field}>
            <div style={styles.passwordLabelRow}>
              <label htmlFor="password" style={styles.label}>
                Password
              </label>
              <a
                href="/forgot-password"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/forgot-password');
                }}
                style={styles.forgotLink}
              >
                Forgot password?
              </a>
            </div>
            <div style={styles.passwordWrapper}>
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
                autoComplete="current-password"
                style={{
                  ...styles.input,
                  ...styles.passwordInput,
                  ...(loading ? styles.inputDisabled : {}),
                }}
                aria-describedby={error ? 'login-error' : undefined}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                disabled={loading}
                style={styles.passwordToggle}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                tabIndex={0}
              >
                {showPassword ? (
                  <EyeOff size={20} style={styles.passwordToggleIcon} />
                ) : (
                  <Eye size={20} style={styles.passwordToggleIcon} />
                )}
              </button>
            </div>
          </div>
          
          {error && (
            <div 
              id="login-error"
              role="alert"
              style={styles.error}
              aria-live="polite"
            >
              {error}
              {siteAccessDenied?.suggestedLoginUrl && isAllowedRedirectUrl(siteAccessDenied.suggestedLoginUrl) && (
                <div style={{ marginTop: '0.75rem' }}>
                  <a href={siteAccessDenied.suggestedLoginUrl} style={{ ...styles.forgotLink, display: 'inline-block' }}>
                    Go to correct site
                  </a>
                  <span style={{ marginLeft: '0.5rem', fontSize: '0.875rem', color: '#6b7280' }}>
                    (redirecting in 2s…)
                  </span>
                </div>
              )}
            </div>
          )}
          
          <button
            type="submit"
            disabled={loading}
            style={{
              ...styles.button,
              ...(loading ? styles.buttonDisabled : {}),
            }}
            aria-busy={loading}
          >
            {loading ? 'Signing in...' : 'Sign in'}
          </button>
        </form>
        
        {/* Subtle trust cue */}
        <div style={styles.trustCue}>
          <span style={styles.trustIcon}>🔒</span>
          <span style={styles.trustText}>Secure sign-in</span>
        </div>
      </div>
    </div>
  );
}
